/**
 * Service pour appeler l'analyse de situation via AppSync GraphQL
 *
 * Architecture asynchrone :
 * 1. Le client appelle analyzeSituation via AppSync
 * 2. La Lambda s'auto-invoque de manière asynchrone et répond immédiatement
 * 3. Les résultats arrivent via la subscription onProjectMessage
 *
 * 2 Actions disponibles :
 * - 'analyze' (défaut) : Analyse de situation avec Perplexity Deep Research
 * - 'scenario' : Élaboration de scénario avec Bedrock Nova
 *
 * Messages reçus via subscription :
 * - scenario_progress : Avancement général (deep_research, ai_analysis, etc.)
 * - scenario_result : Résultats par type d'objet (stakeholders, risks, etc.)
 * - scenario_complete : Résultat complet final
 */

import { useLazyQuery } from '@apollo/client';
import { analyzeSituation as analyzeSituationQuery } from 'graph/queries';

// Action 1 : Analyse de situation (création de projet)
export interface SituationAnalysisRequest {
	projectId: string;
	query: string;
	language?: string; // Langue de réponse (ex: 'fr', 'en')
	scenarios?: Array<{
		label: string;
		content: string;
		horizonValue: number;
		horizonUnit: 'd' | 'w' | 'm' | 'y';
	}>;
}

// Action 2 : Élaboration de scénario
export interface ScenarioElaborationRequest {
	projectId: string;
	query: string; // Hypothèse du scénario (unifié avec query)
	scenarioId?: string; // Scénario de base optionnel
	language?: string; // Langue de réponse (ex: 'fr', 'en')
}

export interface AnalysisResponse {
	success: boolean;
	data?: unknown;
	error?: string;
}

// V2 Scenario generation flow — 3 phases distinctes
export type HorizonUnit = 'd' | 'w' | 'm' | 'y';

export interface ScenarioHorizon {
	value: number;
	unit: HorizonUnit;
}

export interface ScenarioVariant {
	id: string;
	title: string;
	summary: string;
	axis: string;
	trigger: string;
	horizonFocus: string;
}

export interface SuggestHypothesesRequest {
	projectId: string;
	language?: string;
}

export interface ProposeScenarioVariantsRequest {
	projectId: string;
	hypothesis: string;
	horizon: ScenarioHorizon;
	scope?: string;
	focusAxes?: string[];
	language?: string;
}

export interface GenerateScenarioRequest {
	projectId: string;
	scenarioId: string;
	hypothesis: string;
	variant: ScenarioVariant;
	horizon: ScenarioHorizon;
	scope?: string;
	focusAxes?: string[];
	language?: string;
}

/**
 * Hook pour utiliser les fonctionnalités d'analyse
 *
 * Note: La Lambda répond immédiatement (< 30s) et exécute l'analyse en arrière-plan.
 * Les résultats arrivent via la subscription onProjectMessage.
 *
 * Pour gérer les messages reçus, utilisez le hook useScenarioMessages
 * du module scenarioMessages.
 */
export function useAnalyzeSituation() {
	const [analyzeSituationLazyQuery] = useLazyQuery(analyzeSituationQuery);

	/**
	 * Action 1 : Analyse de situation pour création de projet
	 * Utilise Perplexity Deep Research puis Bedrock (Claude) pour structurer les données
	 */
	const analyzeSituation = async (
		request: SituationAnalysisRequest
	): Promise<AnalysisResponse> => {
		try {
			console.log("🔍 Déclenchement de l'analyse de situation:", request);

			const result = await analyzeSituationLazyQuery({
				variables: {
					projectId: request.projectId,
					action: 'analyze',
					query: request.query,
					language: request.language,
					// On envoie toujours le tableau (potentiellement vide) si la propriété
					// est présente, pour que le backend distingue "user a explicitement
					// retiré tous les scénarios" (= 0 scénario) de "appel legacy sans
					// scenarios" (= utiliser les fallbacks).
					scenarios: Array.isArray(request.scenarios)
						? JSON.stringify(request.scenarios)
						: undefined
				}
			});

			console.log(
				'✅ Analyse de situation démarrée (résultats via subscription):',
				result.data
			);

			return {
				success: true,
				data: result.data
			};
		} catch (error) {
			console.error("❌ Erreur lors de l'analyse de situation:", error);

			let errorMessage = 'Erreur inconnue';
			if (error instanceof Error) {
				errorMessage = error.message;
			}

			return {
				success: false,
				error: errorMessage
			};
		}
	};

	/**
	 * Action 2 : Élaboration de scénario
	 * Récupère le contenu du projet, les actualités Perplexity,
	 * puis utilise Bedrock Nova pour proposer les modifications
	 *
	 * Les résultats arrivent progressivement via subscription :
	 * - scenario_progress : étapes d'avancement
	 * - scenario_result : résultats par type d'objet
	 * - scenario_complete : résultat final complet
	 */
	const elaborateScenario = async (
		request: ScenarioElaborationRequest
	): Promise<AnalysisResponse> => {
		try {
			console.log(
				"🎯 Déclenchement de l'élaboration de scénario:",
				request
			);

			const result = await analyzeSituationLazyQuery({
				variables: {
					projectId: request.projectId,
					action: 'scenario',
					query: request.query,
					scenarioId: request.scenarioId,
					language: request.language
				}
			});

			console.log(
				'✅ Élaboration de scénario démarrée (résultats via subscription):',
				result.data
			);

			return {
				success: true,
				data: result.data
			};
		} catch (error) {
			console.error(
				"❌ Erreur lors de l'élaboration de scénario:",
				error
			);

			let errorMessage = 'Erreur inconnue';
			if (error instanceof Error) {
				errorMessage = error.message;
			}

			return {
				success: false,
				error: errorMessage
			};
		}
	};

	/**
	 * V2 — Phase 1 : Suggérer des hypothèses contextuelles à partir du projet
	 * Résultats arrivent via subscription : type='scenario_gen_hypothesis_suggestions'
	 */
	const suggestHypotheses = async (
		request: SuggestHypothesesRequest
	): Promise<AnalysisResponse> => {
		try {
			const result = await analyzeSituationLazyQuery({
				variables: {
					projectId: request.projectId,
					action: 'suggest_hypotheses',
					language: request.language
				}
			});
			return { success: true, data: result.data };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
			console.error('❌ Erreur suggestHypotheses:', error);
			return { success: false, error: errorMessage };
		}
	};

	/**
	 * V2 — Phase 2 : Proposer 4 variantes à partir d'une hypothèse + paramètres
	 * Résultats arrivent via subscription : type='scenario_gen_variants'
	 */
	const proposeScenarioVariants = async (
		request: ProposeScenarioVariantsRequest
	): Promise<AnalysisResponse> => {
		try {
			const result = await analyzeSituationLazyQuery({
				variables: {
					projectId: request.projectId,
					action: 'propose_scenario_variants',
					hypothesis: request.hypothesis,
					language: request.language,
					params: JSON.stringify({
						horizon: request.horizon,
						scope: request.scope || '',
						focusAxes: request.focusAxes || []
					})
				}
			});
			return { success: true, data: result.data };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
			console.error('❌ Erreur proposeScenarioVariants:', error);
			return { success: false, error: errorMessage };
		}
	};

	/**
	 * V2 — Phase 3 : Générer le scénario complet pour une variante choisie
	 * Résultats arrivent via subscription :
	 *   - scenario_gen_progress (étapes)
	 *   - scenario_gen_info (nom/description/impacts)
	 *   - scenario_gen_result (par type d'objet : stakeholders, stakes, risks, indicators)
	 *   - scenario_gen_complete (résultat final)
	 *   - scenario_gen_error en cas d'erreur
	 */
	const generateScenarioFromVariant = async (
		request: GenerateScenarioRequest
	): Promise<AnalysisResponse> => {
		try {
			const result = await analyzeSituationLazyQuery({
				variables: {
					projectId: request.projectId,
					action: 'generate_scenario',
					scenarioId: request.scenarioId,
					hypothesis: request.hypothesis,
					language: request.language,
					params: JSON.stringify({
						variant: request.variant,
						horizon: request.horizon,
						scope: request.scope || '',
						focusAxes: request.focusAxes || []
					})
				}
			});
			return { success: true, data: result.data };
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
			console.error('❌ Erreur generateScenarioFromVariant:', error);
			return { success: false, error: errorMessage };
		}
	};

	return {
		analyzeSituation,
		elaborateScenario,
		suggestHypotheses,
		proposeScenarioVariants,
		generateScenarioFromVariant
	};
}

/**
 * Hook pour déclencher l'analyse de situation pour un projet
 */
export function useTriggerSituationAnalysis() {
	const { analyzeSituation } = useAnalyzeSituation();

	const triggerSituationAnalysis = async (
		organization: string,
		subject: string,
		projectId: string
	): Promise<void> => {
		const analysisRequest: SituationAnalysisRequest = {
			query: `${organization} - ${subject}`,
			projectId: projectId
		};

		console.log(
			"🔍 Déclenchement de l'analyse de situation pour:",
			organization
		);

		const result = await analyzeSituation(analysisRequest);

		if (result.success) {
			console.log(
				'✅ Analyse de situation terminée avec succès:',
				result.data
			);
		} else {
			console.error(
				"❌ Erreur lors de l'analyse de situation:",
				result.error
			);
			throw new Error(
				result.error || "Erreur lors de l'analyse de situation"
			);
		}
	};

	return { triggerSituationAnalysis };
}
